import { ClientLogger } from '../logger/ClientLogger.js';

const gamepadLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const gamepadLogger = ClientLogger.getLogger('input') || gamepadLoggerFallback;

/**
 * GamepadInput.js - Gamepad Input Handler
 *
 * Standard Gamepad API support for Xbox/PS4/PS5 controllers.
 * Handles:
 * - Controller connection/disconnection
 * - Analog stick input (movement, aiming)
 * - Button state tracking (pressed, just pressed, just released)
 * - Trigger input (analog and digital)
 */

class GamepadInput {
  constructor() {
    // Gamepad state
    this.gamepad = null;
    this.connected = false;

    // Configuration - Standard mapping (Xbox/PS4/PS5 compatible)
    this.config = {
      // Axis indices
      axes: {
        leftStickX: 0,
        leftStickY: 1,
        rightStickX: 2,
        rightStickY: 3
      },
      // Button indices (standard mapping)
      buttons: {
        A: 0,              // Bottom face button (A/Cross)
        B: 1,              // Right face button (B/Circle)
        X: 2,              // Left face button (X/Square)
        Y: 3,              // Top face button (Y/Triangle)
        LB: 4,             // Left shoulder
        RB: 5,             // Right shoulder
        LT: 6,             // Left trigger (digital)
        RT: 7,             // Right trigger (digital)
        BACK: 8,           // Back/Select
        START: 9,          // Start
        LEFT_STICK: 10,    // Left stick click
        RIGHT_STICK: 11,   // Right stick click
        DPAD_UP: 12,
        DPAD_DOWN: 13,
        DPAD_LEFT: 14,
        DPAD_RIGHT: 15
      },
      // Deadzone for analog sticks (prevents drift)
      deadzone: 0.15,
      // Trigger threshold (when digital trigger registers as pressed)
      triggerThreshold: 0.1
    };

    // Action mapping - maps game actions to gamepad inputs
    this.actionMap = {
      movement: { type: 'stick', stick: 'left' },
      aim: { type: 'stick', stick: 'right' },
      shoot: { type: 'trigger', button: 'RT' },
      reload: { type: 'button', button: 'X' }, // Reload shares X with pickup
      drift: { type: 'trigger', button: 'LT' }, // Hold to slide
      pickup: { type: 'button', button: 'X' },
      abilityMovement: { type: 'button', button: 'B' },
      abilityUtility: { type: 'button', button: 'Y' },
      inventoryScroll: { type: 'shoulder', buttons: ['LB', 'RB'] },
      pause: { type: 'button', button: 'START' }
    };

    // Button state tracking for press/release detection
    this.buttonStates = {};
    this.lastButtonStates = {};

    // Listen for gamepad connection events
    window.addEventListener('gamepadconnected', (e) => this.onConnect(e));
    window.addEventListener('gamepaddisconnected', (e) => this.onDisconnect(e));
  }

  /**
   * Handle gamepad connection.
   *
   * Only devices the browser reports with the standard mapping are adopted. The
   * Gamepad API also enumerates flight yokes, throttle quadrants, rudder pedals and
   * some wireless-mouse dongles, whose axes mean nothing under the standard layout
   * this class assumes — a throttle quadrant at rest reads as a right stick pinned
   * to the lower-left, which silently takes aim away from the mouse.
   */
  onConnect(event) {
    const gamepad = event.gamepad;
    if (!GamepadInput.isSupportedDevice(gamepad)) {
      gamepadLogger.info(`Ignoring non-gamepad HID device: ${gamepad?.id} (mapping: "${gamepad?.mapping || ''}")`);
      return;
    }
    gamepadLogger.info(`Gamepad connected: ${gamepad.id}`);
    this.gamepad = gamepad;
    this.connected = true;
  }

  /**
   * Handle gamepad disconnection. Devices we never adopted are ignored, so
   * unplugging one does not knock out the controller actually in use.
   */
  onDisconnect(event) {
    if (!this.gamepad || event.gamepad?.index !== this.gamepad.index) return;
    gamepadLogger.info(`Gamepad disconnected: ${event.gamepad.id}`);
    this.gamepad = null;
    this.connected = false;
  }

  /**
   * Poll gamepad state - call this every frame
   */
  poll() {
    if (!this.connected) return;

    // Get latest gamepad state (browsers require re-fetching)
    const gamepads = navigator.getGamepads();
    if (gamepads[this.gamepad?.index]) {
      this.gamepad = gamepads[this.gamepad.index];
    }

    // Update button state tracking
    this.lastButtonStates = { ...this.buttonStates };
    this.buttonStates = {};

    if (this.gamepad) {
      for (const [name, index] of Object.entries(this.config.buttons)) {
        const button = this.gamepad.buttons[index];
        this.buttonStates[name] = button ? button.pressed : false;
      }
    }
  }

  /**
   * Apply deadzone to analog stick value
   * @param {number} value - Raw axis value (-1 to 1)
   * @returns {number} - Deadzone-adjusted value
   */
  applyDeadzone(value) {
    if (Math.abs(value) < this.config.deadzone) return 0;

    // Remap value to account for deadzone (smooth transition)
    const sign = Math.sign(value);
    const magnitude = Math.abs(value);
    const adjusted = (magnitude - this.config.deadzone) / (1 - this.config.deadzone);
    return sign * Math.max(0, Math.min(1, adjusted));
  }

  /**
   * Get left stick input (movement)
   * @returns {{x: number, y: number}} - Normalized stick position
   */
  getLeftStick() {
    if (!this.gamepad) return { x: 0, y: 0 };

    const x = this.applyDeadzone(this.gamepad.axes[this.config.axes.leftStickX] || 0);
    const y = this.applyDeadzone(this.gamepad.axes[this.config.axes.leftStickY] || 0);

    return { x, y };
  }

  /**
   * Get right stick input (aiming)
   * @returns {{x: number, y: number}} - Normalized stick position
   */
  getRightStick() {
    if (!this.gamepad) return { x: 0, y: 0 };

    const x = this.applyDeadzone(this.gamepad.axes[this.config.axes.rightStickX] || 0);
    const y = this.applyDeadzone(this.gamepad.axes[this.config.axes.rightStickY] || 0);

    return { x, y };
  }

  /**
   * Check if button is currently pressed
   * @param {string} buttonName - Button name (A, B, X, Y, etc.)
   * @returns {boolean}
   */
  isButtonPressed(buttonName) {
    return this.buttonStates[buttonName] || false;
  }

  /**
   * Check if button was just pressed this frame
   * @param {string} buttonName - Button name
   * @returns {boolean}
   */
  isButtonJustPressed(buttonName) {
    return this.buttonStates[buttonName] && !this.lastButtonStates[buttonName];
  }

  /**
   * Check if button was just released this frame
   * @param {string} buttonName - Button name
   * @returns {boolean}
   */
  isButtonJustReleased(buttonName) {
    return !this.buttonStates[buttonName] && this.lastButtonStates[buttonName];
  }

  /**
   * Get trigger value (analog 0-1)
   * @param {string} buttonName - Trigger name (LT or RT)
   * @returns {number}
   */
  getTriggerValue(buttonName) {
    if (!this.gamepad) return 0;

    const index = this.config.buttons[buttonName];
    const button = this.gamepad.buttons[index];

    return button ? button.value : 0;
  }

  /**
   * Check if trigger is pressed (digital threshold)
   * @param {string} buttonName - Trigger name
   * @returns {boolean}
   */
  isTriggerPressed(buttonName) {
    return this.getTriggerValue(buttonName) > this.config.triggerThreshold;
  }

  /**
   * Drift (hold-to-slide): left trigger held.
   * @returns {boolean}
   */
  isDriftPressed() {
    return this.isTriggerPressed('LT');
  }

  /**
   * Get movement input from left stick
   * @param {number} threshold - Stick threshold for direction
   * @returns {{up: boolean, down: boolean, left: boolean, right: boolean}}
   */
  getMovementInput(threshold = 0.3) {
    const stick = this.getLeftStick();
    return {
      up: stick.y < -threshold,
      down: stick.y > threshold,
      left: stick.x < -threshold,
      right: stick.x > threshold
    };
  }

  /**
   * Get aim angle from right stick (if active)
   * @param {number} threshold - Minimum stick magnitude to register
   * @returns {{active: boolean, angle: number}}
   */
  getAimInput(threshold = 0.3) {
    const stick = this.getRightStick();
    const magnitude = Math.sqrt(stick.x * stick.x + stick.y * stick.y);

    if (magnitude > threshold) {
      return {
        active: true,
        angle: Math.atan2(stick.y, stick.x)
      };
    }

    return { active: false, angle: 0 };
  }
}

/**
 * Whether a Gamepad API device should drive gameplay input. The standard mapping is
 * the contract this class is written against (axes 0-3 = sticks, buttons 0-15 = the
 * Xbox layout); anything else is some other HID device wearing a gamepad's clothes.
 */
GamepadInput.isSupportedDevice = (gamepad) => gamepad?.mapping === 'standard';

// Export for use in other modules
// For now, GamepadInput is a global class loaded via script tag

export { gamepadLoggerFallback, gamepadLogger, GamepadInput };
